import React, { useRef, useState, useEffect } from 'react';
import { 
  ToolsIcon, 
  SettingsIcon,
  ExecuteIcon,
  FolderIcon,
  SaveIcon,
  OpenIcon,
  SwarmIcon,
  GraphIcon,
  TargetIcon,
  PlusIcon
} from './Icons';
import Tooltip from './Tooltip';
import useAgentConfigStore from '../stores/agentConfigStore';
import KnowledgeBaseManager from './KnowledgeBaseManager';
import { formatErrorMessage } from '../utils/errorUtils';

function Toolbar({ 
  onAddNode, 
  onSave, 
  onLoad, 
  workflowType, 
  onWorkflowTypeChange, 
  onCreateGroup, 
  selectedNodesCount,
  onToggleLeftPanel,
  onToggleRightPanel,
  onToggleBottomPanel,
  leftPanelOpen,
  rightPanelOpen,
  bottomPanelOpen,
  onKnowledgeBaseChange
}) {
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Workflow dropdown state
  const [showWorkflowDropdown, setShowWorkflowDropdown] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showKBManager, setShowKBManager] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // Store state
  const {
    workflowName,
    workflowId,
    isSystem,
    isDirty,
    presets,
    presetsLoading,
    savePreset,
    saveAsNewPreset,
    loadPreset,
    deletePreset,
  } = useAgentConfigStore();

  // Track if we've fetched presets for the load dialog
  const hasFetchedForLoad = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowWorkflowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset fetch flag when dialog closes
  useEffect(() => {
    if (!showLoadDialog) {
      hasFetchedForLoad.current = false;
    }
  }, [showLoadDialog]);

  // Fetch presets when load dialog opens (only once per dialog open)
  useEffect(() => {
    if (showLoadDialog && !hasFetchedForLoad.current && !presetsLoading) {
      hasFetchedForLoad.current = true;
      // Get fetchPresets directly from store to avoid stale closure
      useAgentConfigStore.getState().fetchPresets();
    }
  }, [showLoadDialog, presetsLoading]);

  const handleLoadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle save (update existing or fork system)
  const handleSave = async () => {
    if (isSystem) {
      // Fork system preset - show save dialog
      setNewPresetName(workflowName + ' (Copy)');
      setShowSaveDialog(true);
    } else if (workflowId) {
      // Update existing
      try {
        setSaving(true);
        await savePreset();
        setShowWorkflowDropdown(false);
      } catch (error) {
        setSaveError(error.message);
      } finally {
        setSaving(false);
      }
    } else {
      // New preset - show save dialog
      setNewPresetName(workflowName || 'My Workflow');
      setShowSaveDialog(true);
    }
    setShowWorkflowDropdown(false);
  };

  // Handle save as new
  const handleSaveAs = () => {
    setNewPresetName(workflowName || 'My Workflow');
    setShowSaveDialog(true);
    setShowWorkflowDropdown(false);
  };

  // Handle save dialog confirm
  const handleSaveConfirm = async () => {
    if (!newPresetName.trim()) return;
    
    try {
      setSaving(true);
      setSaveError(null);
      await saveAsNewPreset(newPresetName.trim());
      setShowSaveDialog(false);
      setNewPresetName('');
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle load preset
  const handleLoadPreset = async (presetId) => {
    try {
      await loadPreset(presetId);
      setShowLoadDialog(false);
    } catch (error) {
      console.error('Failed to load preset:', error);
    }
  };

  // Handle delete preset
  const handleDeletePreset = async (presetId, presetName, isSystemPreset) => {
    if (isSystemPreset) {
      console.warn('Cannot delete system presets');
      return;
    }
    
    if (window.confirm(`Delete "${presetName}"? This cannot be undone.`)) {
      try {
        await deletePreset(presetId);
      } catch (error) {
        console.error('Failed to delete preset:', error);
      }
    }
  };

  return (
    <>
      <div className="top-toolbar">
        {/* Panel Toggle Icons */}
        <Tooltip text="Toggle Tool Library (Ctrl+1)">
          <button 
            className={`toolbar-btn icon-btn ${leftPanelOpen ? 'active' : ''}`}
            onClick={onToggleLeftPanel}
          >
            <ToolsIcon size={16} color={leftPanelOpen ? '#000' : '#999'} />
          </button>
        </Tooltip>
        <Tooltip text="Toggle Properties (Ctrl+2)">
          <button 
            className={`toolbar-btn icon-btn ${rightPanelOpen ? 'active' : ''}`}
            onClick={onToggleRightPanel}
          >
            <SettingsIcon size={16} color={rightPanelOpen ? '#000' : '#999'} />
          </button>
        </Tooltip>
        <Tooltip text="Toggle Execution (Ctrl+3)">
          <button 
            className={`toolbar-btn icon-btn ${bottomPanelOpen ? 'active' : ''}`}
            onClick={onToggleBottomPanel}
          >
            <ExecuteIcon size={16} color={bottomPanelOpen ? '#000' : '#999'} />
          </button>
        </Tooltip>

        <div className="toolbar-divider" />

        {/* Group Action Icons */}
        <Tooltip text="Create Visual Group (select 2+ nodes)">
          <button 
            className="toolbar-btn icon-btn"
            onClick={() => onCreateGroup('visual')}
            disabled={selectedNodesCount < 2}
            style={{ opacity: selectedNodesCount < 2 ? 0.4 : 1 }}
          >
            <FolderIcon size={16} color="#999" />
          </button>
        </Tooltip>
        <Tooltip text="Create Swarm Group (select 2+ agents)">
          <button 
            className="toolbar-btn icon-btn"
            onClick={() => onCreateGroup('swarm')}
            disabled={selectedNodesCount < 2}
            style={{ opacity: selectedNodesCount < 2 ? 0.4 : 1 }}
          >
            <SwarmIcon size={16} color="#999" />
          </button>
        </Tooltip>
        <Tooltip text="Create Graph Group (select 2+ agents)">
          <button 
            className="toolbar-btn icon-btn"
            onClick={() => onCreateGroup('graph')}
            disabled={selectedNodesCount < 2}
            style={{ opacity: selectedNodesCount < 2 ? 0.4 : 1 }}
          >
            <GraphIcon size={16} color="#999" />
          </button>
        </Tooltip>

        <div className="toolbar-divider" />

        {/* Workflow Preset Dropdown */}
        <div className="workflow-dropdown-container" ref={dropdownRef}>
          <button 
            className={`toolbar-btn workflow-preset-btn ${isDirty ? 'dirty' : ''}`}
            onClick={() => setShowWorkflowDropdown(!showWorkflowDropdown)}
            title={isSystem ? 'System Preset (read-only)' : 'User Preset'}
          >
            <span className="preset-name">
              {workflowName}
              {isDirty && <span className="dirty-indicator">*</span>}
            </span>
            {isSystem && <span className="system-badge">System</span>}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 4 }}>
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          {showWorkflowDropdown && (
            <div className="workflow-dropdown">
              <button onClick={handleSave} disabled={saving}>
                <SaveIcon size={14} />
                {isSystem ? 'Save as Copy...' : 'Save'}
              </button>
              <button onClick={handleSaveAs}>
                <SaveIcon size={14} />
                Save As New...
              </button>
              <div className="dropdown-divider" />
              <button onClick={() => { setShowLoadDialog(true); setShowWorkflowDropdown(false); }}>
                <OpenIcon size={14} />
                Load Preset...
              </button>
              <div className="dropdown-divider" />
              <button onClick={handleLoadClick}>
                <OpenIcon size={14} />
                Import from File...
              </button>
              <button onClick={onSave}>
                <SaveIcon size={14} />
                Export to File...
              </button>
              <div className="dropdown-divider" />
              <button onClick={() => { setShowKBManager(true); setShowWorkflowDropdown(false); }}>
                <ToolsIcon size={14} />
                Knowledge Bases...
              </button>
            </div>
          )}
        </div>

        <input 
          ref={fileInputRef}
          type="file" 
          accept=".json" 
          onChange={onLoad} 
          style={{ display: 'none' }}
        />

        {/* Workflow Type Selector - styled like Built-in/Custom tabs */}
        <div className="workflow-tabs">
          <Tooltip text="Single Agent">
            <button
              className={`workflow-tab ${workflowType === 'single' ? 'active' : ''}`}
              onClick={() => onWorkflowTypeChange('single')}
            >
              <TargetIcon size={14} />
              <span>Single</span>
            </button>
          </Tooltip>
          <Tooltip text="Swarm Mode">
            <button
              className={`workflow-tab ${workflowType === 'swarm' ? 'active' : ''}`}
              onClick={() => onWorkflowTypeChange('swarm')}
            >
              <SwarmIcon size={14} />
              <span>Swarm</span>
            </button>
          </Tooltip>
          <Tooltip text="Graph Mode">
            <button
              className={`workflow-tab ${workflowType === 'graph' ? 'active' : ''}`}
              onClick={() => onWorkflowTypeChange('graph')}
            >
              <GraphIcon size={14} />
              <span>Graph</span>
            </button>
          </Tooltip>
        </div>

        {/* Main Action Button - Primary CTA */}
        <Tooltip text="Add new agent node">
          <button 
            className="toolbar-btn primary"
            onClick={() => onAddNode('agent')}
          >
            <PlusIcon size={16} color="#fff" />
            <span>Add Agent</span>
          </button>
        </Tooltip>
      </div>

      {/* Save Dialog Modal */}
      {showSaveDialog && (
        <div className="modal-overlay" onClick={() => setShowSaveDialog(false)}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <h3>Save Workflow Preset</h3>
            <input
              type="text"
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              placeholder="Preset name..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveConfirm();
                if (e.key === 'Escape') setShowSaveDialog(false);
              }}
            />
            {saveError && <div className="error-message">{formatErrorMessage(saveError)}</div>}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveConfirm}
                disabled={saving || !newPresetName.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Dialog Modal */}
      {showLoadDialog && (
        <div className="modal-overlay" onClick={() => setShowLoadDialog(false)}>
          <div className="modal-dialog preset-list-dialog" onClick={e => e.stopPropagation()}>
            <h3>Load Workflow Preset</h3>
            
            {presetsLoading ? (
              <div className="loading-message">Loading presets...</div>
            ) : presets.length === 0 ? (
              <div className="empty-message">No presets available</div>
            ) : (
              <div className="preset-list">
                {/* System presets section */}
                {presets.filter(p => p.is_system).length > 0 && (
                  <>
                    <div className="preset-section-header">System Presets</div>
                    {presets.filter(p => p.is_system).map(preset => (
                      <div 
                        key={preset.id} 
                        className={`preset-item ${workflowId === preset.id ? 'active' : ''}`}
                      >
                        <div className="preset-info" onClick={() => handleLoadPreset(preset.id)}>
                          <div className="preset-name">{preset.name}</div>
                          {preset.description && (
                            <div className="preset-description">{preset.description}</div>
                          )}
                        </div>
                        <span className="system-badge">System</span>
                      </div>
                    ))}
                  </>
                )}

                {/* User presets section */}
                {presets.filter(p => !p.is_system).length > 0 && (
                  <>
                    <div className="preset-section-header">My Presets</div>
                    {presets.filter(p => !p.is_system).map(preset => (
                      <div 
                        key={preset.id} 
                        className={`preset-item ${workflowId === preset.id ? 'active' : ''}`}
                      >
                        <div className="preset-info" onClick={() => handleLoadPreset(preset.id)}>
                          <div className="preset-name">{preset.name}</div>
                          {preset.description && (
                            <div className="preset-description">{preset.description}</div>
                          )}
                        </div>
                        <button 
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePreset(preset.id, preset.name, preset.is_system);
                          }}
                          title="Delete preset"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowLoadDialog(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Base Manager Modal */}
      <KnowledgeBaseManager
        isOpen={showKBManager}
        onClose={() => setShowKBManager(false)}
        onKnowledgeBaseChange={onKnowledgeBaseChange}
      />
    </>
  );
}

export default Toolbar;
